import React, { useCallback, useEffect, useMemo, useState } from "react";

import axios from "axios";

import { AlertTriangle, CheckCircle2, Download, Gauge, Loader2, RefreshCw, ScrollText, Shield, Trash2, XCircle } from "lucide-react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { Switch } from "@/components/ui/switch";

import { Badge } from "@/components/ui/badge";

import { Checkbox } from "@/components/ui/checkbox";

import CdrTablePagination from "@/components/cdr/CdrTablePagination";

import {

  PanelTableBody, PanelTableCell, PanelTableFrame, PanelTableHead, PanelTableHeadRow, PanelTableHeader, PanelTableRow,

} from "@/components/ui/panel-table";



const PAGE_SIZE = 25;



const formatBytes = (bytes) => {

  const n = Number(bytes) || 0;

  if (n <= 0) return "—";

  if (n < 1024) return `${n} B`;

  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;

  return `${(n / (1024 * 1024)).toFixed(2)} MB`;

};



const formatDate = (value) => {

  if (!value) return "—";

  try {

    return new Date(String(value).replace(" ", "T")).toLocaleString("tr-TR");

  } catch {

    return value;

  }

};



const ERROR_CODE_LABELS = {

  AccessDenied: "AWS erişim reddi",

  NoSuchKey: "Dosya yok",

  NotFound: "Bulunamadı",

  NOT_FOUND: "Bulunamadı",

  FILE_NOT_FOUND: "Bulunamadı",

  RESOLVE_FAILED: "Kayıt çözümlenemedi",

  INVALID_URL: "Geçersiz adres",

  PROBE_FAILED: "Kaynak kontrol hatası",

  STREAM_FAILED: "Aktarım hatası",

  SERVER_ERROR: "Sunucu hatası",

  DOWNLOAD_FAILED: "İndirme hatası",

};



const RecordingDownloadSettings = ({ apiBase, isDark, textClass, labelClass }) => {

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [logsLoading, setLogsLoading] = useState(true);

  const [deleting, setDeleting] = useState(false);

  const [settings, setSettings] = useState({

    throttle_enabled: true,

    throttle_kbps: 64,

    log_enabled: true,

  });

  const [items, setItems] = useState([]);

  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);

  const [successCount, setSuccessCount] = useState(0);

  const [failedCount, setFailedCount] = useState(0);

  const [users, setUsers] = useState([]);

  const [search, setSearch] = useState("");

  const [userId, setUserId] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");

  const [startDate, setStartDate] = useState("");

  const [endDate, setEndDate] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);



  const panelClass = isDark ? "border-slate-800 bg-slate-900/60" : "border-gray-200 bg-white";

  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-200";

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));



  const filterParams = useMemo(() => ({

    search: search.trim() || undefined,

    user_id: userId || undefined,

    status: statusFilter !== "all" ? statusFilter : undefined,

    start_date: startDate || undefined,

    end_date: endDate || undefined,

  }), [search, userId, statusFilter, startDate, endDate]);



  const loadSettings = useCallback(async () => {

    setLoading(true);

    try {

      const res = await axios.get(`${apiBase}/recording-download-settings`);

      const s = res.data?.settings || {};

      setSettings({

        throttle_enabled: !!s.throttle_enabled,

        throttle_kbps: Number(s.throttle_kbps) || 64,

        log_enabled: s.log_enabled !== false && s.log_enabled !== 0,

      });

    } catch (err) {

      toast.error(err.response?.data?.detail || "Ayarlar yüklenemedi");

    } finally {

      setLoading(false);

    }

  }, [apiBase]);



  const loadLogs = useCallback(async () => {

    setLogsLoading(true);

    try {

      const res = await axios.get(`${apiBase}/recording-download-settings`, {

        params: {

          type: "logs",

          limit: PAGE_SIZE,

          offset: (page - 1) * PAGE_SIZE,

          ...filterParams,

        },

      });

      setItems(res.data?.items || []);

      setTotal(res.data?.total || 0);

      setSuccessCount(res.data?.success_count || 0);

      setFailedCount(res.data?.failed_count || 0);

    } catch (err) {

      toast.error(err.response?.data?.detail || "Loglar yüklenemedi");

    } finally {

      setLogsLoading(false);

    }

  }, [apiBase, page, filterParams]);



  useEffect(() => {

    loadSettings();

    axios.get(`${apiBase}/users`).then((res) => setUsers(res.data || [])).catch(() => {});

  }, [apiBase, loadSettings]);



  useEffect(() => {

    loadLogs();

  }, [loadLogs]);



  useEffect(() => {

    setPage(1);

    setSelectedIds([]);

  }, [search, userId, statusFilter, startDate, endDate]);



  useEffect(() => {

    setSelectedIds([]);

  }, [page]);



  const pageIds = useMemo(() => items.map((row) => row.id), [items]);

  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));



  const toggleSelectAll = () => {

    if (allPageSelected) {

      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));

    } else {

      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);

    }

  };



  const toggleSelectOne = (id) => {

    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  };



  const handleDelete = async ({ ids = [], deleteFiltered = false } = {}) => {

    const confirmText = deleteFiltered

      ? `Mevcut filtreye uyan ${total.toLocaleString("tr-TR")} indirme logu silinecek. Devam edilsin mi?`

      : `${ids.length} kayıt silinecek. Devam edilsin mi?`;

    if (!window.confirm(confirmText)) return;



    setDeleting(true);

    try {

      const payload = deleteFiltered

        ? { delete_filtered: true, ...filterParams }

        : { ids };

      const res = await axios.delete(`${apiBase}/recording-download-settings`, { data: payload });

      toast.success(res.data?.message || "Kayıtlar silindi");

      setSelectedIds([]);

      if (deleteFiltered) setPage(1);

      await loadLogs();

    } catch (err) {

      toast.error(err.response?.data?.detail || "Silme işlemi başarısız");

    } finally {

      setDeleting(false);

    }

  };



  const handleSave = async () => {

    setSaving(true);

    try {

      const res = await axios.put(`${apiBase}/recording-download-settings`, {

        throttle_enabled: settings.throttle_enabled,

        throttle_kbps: Math.max(8, Math.min(2048, Number(settings.throttle_kbps) || 64)),

        log_enabled: settings.log_enabled,

      });

      const s = res.data?.settings || {};

      setSettings({

        throttle_enabled: !!s.throttle_enabled,

        throttle_kbps: Number(s.throttle_kbps) || 64,

        log_enabled: !!s.log_enabled,

      });

      toast.success("Ses indirme ayarları kaydedildi");

    } catch (err) {

      toast.error(err.response?.data?.detail || "Kaydedilemedi");

    } finally {

      setSaving(false);

    }

  };



  const failedItems = useMemo(() => items.filter((row) => !row.success), [items]);



  if (loading) {

    return (

      <div className="flex justify-center py-16">

        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />

      </div>

    );

  }



  return (

    <div className="space-y-6">

      <div className={`rounded-2xl border overflow-hidden ${panelClass}`}>

        <div className={`px-4 py-4 border-b ${isDark ? "border-slate-800" : "border-gray-100"}`}>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">

            <div className="flex items-center gap-3">

              <div className={`p-2 rounded-xl ${isDark ? "bg-amber-500/15 text-amber-400" : "bg-amber-50 text-amber-600"}`}>

                <Gauge className="w-5 h-5" />

              </div>

              <div>

                <h3 className={`text-base font-semibold ${textClass}`}>Ses İndirme Ayarları</h3>

                <p className={`text-xs ${labelClass}`}>Panelden ses dosyası indirmelerini yavaşlat ve kayıt altına al</p>

              </div>

            </div>

            <Button type="button" onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">

              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}

              Kaydet

            </Button>

          </div>

        </div>



        <div className="p-4 space-y-4">

          <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/70"}`}>

            <div>

              <p className={`text-sm font-medium ${textClass}`}>İndirme hızını yavaşlat</p>

              <p className={`text-xs mt-1 ${labelClass}`}>Açıkken ses dosyaları panel üzerinden sınırlı hızda indirilir. Dinleme etkilenmez. Admin kullanıcıları hız limitine takılmaz.</p>

            </div>

            <Switch checked={settings.throttle_enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, throttle_enabled: v }))} />

          </div>



          <div className={`rounded-xl border p-4 space-y-3 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/70"}`}>

            <Label className={labelClass}>Maksimum indirme hızı (KB/s)</Label>

            <Input

              type="number"

              min={8}

              max={2048}

              value={settings.throttle_kbps}

              onChange={(e) => setSettings((s) => ({ ...s, throttle_kbps: e.target.value }))}

              className={`max-w-xs ${inputClass}`}

              disabled={!settings.throttle_enabled}

            />

            <p className={`text-xs ${labelClass}`}>Önerilen: 32–128 KB/s. Düşük değer = daha yavaş indirme.</p>

          </div>



          <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/70"}`}>

            <div>

              <p className={`text-sm font-medium ${textClass}`}>İndirme loglarını kaydet</p>

              <p className={`text-xs mt-1 ${labelClass}`}>Başarılı ve başarısız tüm indirme denemeleri kaydedilir.</p>

            </div>

            <Switch checked={settings.log_enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, log_enabled: v }))} />

          </div>

        </div>

      </div>



      <div className={`rounded-2xl border overflow-hidden ${panelClass}`}>

        <div className={`px-4 py-4 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 ${isDark ? "border-slate-800" : "border-gray-100"}`}>

          <div className="flex items-center gap-3">

            <div className={`p-2 rounded-xl ${isDark ? "bg-violet-500/15 text-violet-400" : "bg-violet-50 text-violet-600"}`}>

              <ScrollText className="w-5 h-5" />

            </div>

            <div>

              <h3 className={`text-base font-semibold ${textClass}`}>Ses İndirme Logları</h3>

              <p className={`text-xs ${labelClass}`}>Başarılı indirmeler ve hata kayıtları</p>

            </div>

          </div>

          <Button type="button" size="sm" variant="outline" onClick={loadLogs} disabled={logsLoading || deleting} className={isDark ? "border-slate-700" : ""}>

            {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}

          </Button>

        </div>



        <div className={`px-4 py-3 border-b grid grid-cols-1 sm:grid-cols-3 gap-2 ${isDark ? "border-slate-800 bg-slate-950/20" : "border-gray-100 bg-gray-50/70"}`}>

          <div className={`rounded-lg border px-3 py-2 ${isDark ? "border-emerald-500/30 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50"}`}>

            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">

              <CheckCircle2 className="w-4 h-4" />

              <span className="text-xs font-medium">Başarılı</span>

            </div>

            <p className={`text-lg font-bold mt-1 ${textClass}`}>{successCount}</p>

          </div>

          <div className={`rounded-lg border px-3 py-2 ${isDark ? "border-rose-500/40 bg-rose-500/10" : "border-rose-200 bg-rose-50"}`}>

            <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400">

              <XCircle className="w-4 h-4" />

              <span className="text-xs font-medium">Başarısız / Hata</span>

            </div>

            <p className={`text-lg font-bold mt-1 ${textClass}`}>{failedCount}</p>

          </div>

          <div className={`rounded-lg border px-3 py-2 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-white"}`}>

            <div className={`flex items-center gap-2 ${labelClass}`}>

              <ScrollText className="w-4 h-4" />

              <span className="text-xs font-medium">Toplam kayıt</span>

            </div>

            <p className={`text-lg font-bold mt-1 ${textClass}`}>{total}</p>

          </div>

        </div>



        {failedCount > 0 && statusFilter !== "success" && (

          <div className={`mx-4 mt-3 rounded-xl border px-3 py-2.5 flex items-start gap-2 ${isDark ? "border-rose-500/40 bg-rose-500/10 text-rose-100" : "border-rose-200 bg-rose-50 text-rose-900"}`}>

            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />

            <div className="text-xs">

              <p className="font-semibold">Başarısız indirme denemeleri tespit edildi</p>

              <p className={`mt-0.5 ${isDark ? "text-rose-200/90" : "text-rose-800"}`}>

                Filtrede {failedCount} hata kaydı var. Kimin hangi IP/cihazdan hangi hatayı aldığını aşağıdaki tabloda görebilirsiniz.

              </p>

            </div>

          </div>

        )}



        <div className={`px-4 py-3 border-b flex flex-col xl:flex-row gap-2 ${isDark ? "border-slate-800 bg-slate-950/20" : "border-gray-100 bg-gray-50/70"}`}>

          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kullanıcı, dosya, hata, IP..." className={`h-9 ${inputClass}`} />

          <select

            value={userId}

            onChange={(e) => setUserId(e.target.value)}

            className={`h-9 rounded-md border px-2 text-sm min-w-[180px] ${inputClass}`}

          >

            <option value="">Tüm kullanıcılar</option>

            {users.map((u) => (

              <option key={u.id} value={u.id}>{u.ad_soyad || u.kullanici_adi || `#${u.id}`}</option>

            ))}

          </select>

          <select

            value={statusFilter}

            onChange={(e) => setStatusFilter(e.target.value)}

            className={`h-9 rounded-md border px-2 text-sm min-w-[140px] ${inputClass}`}

          >

            <option value="all">Tüm durumlar</option>

            <option value="success">Yalnızca başarılı</option>

            <option value="failed">Yalnızca hatalı</option>

          </select>

          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`h-9 md:w-40 ${inputClass}`} />

          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`h-9 md:w-40 ${inputClass}`} />

          <Button type="button" size="sm" onClick={() => { setPage(1); loadLogs(); }} className="h-9">Filtrele</Button>

        </div>



        {(selectedIds.length > 0 || total > 0) && (

          <div className={`px-4 py-2 border-b flex flex-wrap items-center gap-2 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/80"}`}>

            {selectedIds.length > 0 && (

              <>

                <span className={`text-xs ${labelClass}`}>{selectedIds.length} seçili</span>

                <Button type="button" size="sm" variant="destructive" disabled={deleting} onClick={() => handleDelete({ ids: selectedIds })} className="h-8">

                  {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}

                  Seçilenleri sil

                </Button>

              </>

            )}

            {total > 0 && (

              <Button

                type="button"

                size="sm"

                variant="outline"

                disabled={deleting}

                onClick={() => handleDelete({ deleteFiltered: true })}

                className={`h-8 ${isDark ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}

              >

                Filtreye uyanları sil ({total.toLocaleString("tr-TR")})

              </Button>

            )}

          </div>

        )}



        {logsLoading ? (

          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>

        ) : (

          <PanelTableFrame className="border-0 rounded-none">

            <PanelTableHeader>

              <PanelTableHeadRow>

                <PanelTableHead className="w-10">

                  <Checkbox checked={allPageSelected} onCheckedChange={toggleSelectAll} aria-label="Sayfadaki tümünü seç" />

                </PanelTableHead>

                <PanelTableHead>Durum</PanelTableHead>

                <PanelTableHead>Zaman</PanelTableHead>

                <PanelTableHead>Kullanıcı</PanelTableHead>

                <PanelTableHead>Kayıt ID</PanelTableHead>

                <PanelTableHead>Dosya</PanelTableHead>

                <PanelTableHead>Hata / Detay</PanelTableHead>

                <PanelTableHead>IP</PanelTableHead>

                <PanelTableHead>Cihaz</PanelTableHead>

              </PanelTableHeadRow>

            </PanelTableHeader>

            <PanelTableBody>

              {items.length === 0 ? (

                <PanelTableRow>

                  <PanelTableCell colSpan={9} className={`text-center py-10 ${labelClass}`}>Kayıt yok</PanelTableCell>

                </PanelTableRow>

              ) : items.map((row) => {

                const isFailed = !row.success;

                const rowClass = isFailed

                  ? (isDark ? "bg-rose-500/10 hover:bg-rose-500/15" : "bg-rose-50/80 hover:bg-rose-50")

                  : "";

                return (

                  <PanelTableRow key={row.id} className={rowClass}>

                    <PanelTableCell>

                      <Checkbox checked={selectedIds.includes(row.id)} onCheckedChange={() => toggleSelectOne(row.id)} aria-label={`Kayıt ${row.id} seç`} />

                    </PanelTableCell>

                    <PanelTableCell>

                      {row.success ? (

                        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/20">

                          <CheckCircle2 className="w-3 h-3 mr-1" /> Başarılı

                        </Badge>

                      ) : row.not_found ? (

                        <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">

                          <AlertTriangle className="w-3 h-3 mr-1" /> Bulunamadı

                        </Badge>

                      ) : (

                        <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/30">

                          <XCircle className="w-3 h-3 mr-1" /> Hata

                        </Badge>

                      )}

                    </PanelTableCell>

                    <PanelTableCell className="text-xs whitespace-nowrap">{formatDate(row.created_at)}</PanelTableCell>

                    <PanelTableCell className="text-xs">

                      <div className="font-medium">{row.display_name || row.username}</div>

                      {row.username && row.display_name && <div className={labelClass}>{row.username}</div>}

                    </PanelTableCell>

                    <PanelTableCell className="text-xs font-mono">{row.cdr_id ? `#${row.cdr_id}` : "—"}</PanelTableCell>

                    <PanelTableCell className="text-xs max-w-[180px] truncate" title={row.file_name || "—"}>

                      <span className="inline-flex items-center gap-1">

                        <Download className="w-3 h-3 shrink-0 opacity-60" />

                        {row.file_name || "—"}

                      </span>

                    </PanelTableCell>

                    <PanelTableCell className="text-xs max-w-[280px]">

                      {isFailed ? (

                        <div className="space-y-1">

                          {row.error_code && (

                            <Badge variant="outline" className={isDark ? "border-rose-500/40 text-rose-300" : "border-rose-300 text-rose-700"}>

                              {ERROR_CODE_LABELS[row.error_code] || row.error_code}

                            </Badge>

                          )}

                          <p className={`leading-snug ${isDark ? "text-rose-200" : "text-rose-800"}`} title={row.error_message}>

                            {row.error_message || "Bilinmeyen hata"}

                          </p>

                        </div>

                      ) : (

                        <span className={labelClass}>{formatBytes(row.file_size)} · {row.duration_ms}ms</span>

                      )}

                    </PanelTableCell>

                    <PanelTableCell className="text-xs font-mono">{row.ip_address}</PanelTableCell>

                    <PanelTableCell className="text-xs">{row.device_label || "—"}</PanelTableCell>

                  </PanelTableRow>

                );

              })}

            </PanelTableBody>

          </PanelTableFrame>

        )}



        {!logsLoading && failedItems.length > 0 && statusFilter !== "success" && (

          <div className={`px-4 py-3 border-t text-xs ${isDark ? "border-slate-800 text-slate-400" : "border-gray-100 text-gray-500"}`}>

            Bu sayfada {failedItems.length} başarısız kayıt listeleniyor.

          </div>

        )}



        <CdrTablePagination

          isDark={isDark}

          page={page}

          pageSize={PAGE_SIZE}

          total={total}

          totalPages={totalPages}

          loading={logsLoading || deleting}

          onPageChange={setPage}

        />

      </div>

    </div>

  );

};



export default RecordingDownloadSettings;

